import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { sentryFeedback } from "@gui/infrastructure/pass-through/sentryFeedback";
import { UserContext } from "@gui/contexts/user/UserContext";
import { ISSUE_COUNT_PATHS, useIssueCounts } from "@gui/contexts/issueCounts/IssueCountsContext";
import { useAuthUseCases } from "@gui/contexts/useCases/UseCasesContext";
import { API } from "@gui/infrastructure/pass-through/api";
import { visibleNavSections, type User } from "@domain/Auth";
import {
	type NavLink,
	USER_LINKS,
	LOGIN_LINK,
	ADMIN_LINKS,
	ISSUES_MENU,
	UTILITIES_MENU,
	DEVELOPER_MENU,
} from "./navLinks";

export type TopItem =
	| { kind: "divider"; key: string }
	| { kind: "nav"; key: string; link: NavLink }
	| { kind: "submenu"; key: string; label: string; icon: React.ReactNode; links: NavLink[] }
	| { kind: "logout"; key: string };

interface DrawerNavigationViewModelInput {
	onNavigate: (link: NavLink) => void;
	mobileOpen: boolean;
	onMobileClose: () => void;
	/** True when the responsive drawer is in mobile mode — derived in the view via MUI's useMediaQuery. */
	isMobile: boolean;
	/** View-side close-and-collapse helper called after every nav click / logout / clear-cache. */
	closeMenusOnNav: () => void;
}

interface ItemBadgeState {
	hasFetcher: boolean;
	count: number | undefined;
	isFetching: boolean;
	showCountBadge: boolean;
}

interface OverflowBadgeState {
	count: number;
	isFetching: boolean;
	showCountBadge: boolean;
}

/**
 * Drives DrawerNavigation. Owns the top-items derivation (links visible to
 * the current user given role flags), the click handlers (navigate + close),
 * the admin-only Clear Cache + Feedback handlers, and per-item badge state.
 *
 * The view retains DOM refs and the useLayoutEffect that measures the drawer
 * to compute the overflow cutoff — those need direct DOM access.
 */
export function useDrawerNavigationViewModel(input: DrawerNavigationViewModelInput) {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const user = React.useContext(UserContext) as User | undefined;
	const { counts, loading: issueCountsLoading } = useIssueCounts();
	const { auth } = useAuthUseCases();

	const companyParam = searchParams.get("company");
	const buildNavPath = (path: string) => (companyParam ? `${path}?company=${companyParam}` : path);

	const topItems: TopItem[] = React.useMemo(() => {
		const sections = visibleNavSections(user);
		const items: TopItem[] = [];
		const links = sections.isLoggedIn ? USER_LINKS : [LOGIN_LINK];
		links.forEach((link) => items.push({ kind: "nav", key: link.to, link }));
		// Logout now lives in the toolbar's top-right account menu (see Toolbar.tsx), not the sidebar.
		if (sections.showAdmin) {
			items.push({ kind: "divider", key: "div-admin" });
			ADMIN_LINKS.forEach((link) => {
				items.push({ kind: "nav", key: link.to, link });
				// Issues submenu sits directly beneath Job Diagnostics.
				if (link.to === "/admin/job-diagnostics") {
					items.push({
						kind: "submenu",
						key: "issues-menu",
						label: ISSUES_MENU.label,
						icon: ISSUES_MENU.icon,
						links: ISSUES_MENU.links,
					});
				}
			});
			items.push({
				kind: "submenu",
				key: "utils-menu",
				label: UTILITIES_MENU.label,
				icon: UTILITIES_MENU.icon,
				links: UTILITIES_MENU.links,
			});
		}
		if (sections.showDeveloper) {
			items.push({ kind: "divider", key: "div-dev" });
			items.push({
				kind: "submenu",
				key: "dev-menu",
				label: DEVELOPER_MENU.label,
				icon: DEVELOPER_MENU.icon,
				links: DEVELOPER_MENU.links,
			});
		}
		return items;
	}, [user]);

	const handleNavClick = (link: NavLink) => {
		input.onNavigate(link);
		navigate(buildNavPath(link.to), { state: { title: link.text } });
		input.closeMenusOnNav();
	};

	const handleLogout = () => {
		auth.logout();
		// Hard reload to /login so all in-memory caches reset alongside the cleared tokens.
		window.location.href = "/login";
		input.closeMenusOnNav();
	};

	const handleClearCache = () => {
		API.Data.clearCache();
		if (input.isMobile) input.onMobileClose();
	};

	const handleOpenFeedback = async () => {
		await sentryFeedback.openForm();
		if (input.isMobile) input.onMobileClose();
	};

	const getItemBadgeState = (navPath: string | undefined): ItemBadgeState => {
		const hasFetcher = navPath ? ISSUE_COUNT_PATHS.has(navPath) : false;
		const count = navPath ? counts[navPath] : undefined;
		const isFetching = hasFetcher && count === undefined && issueCountsLoading;
		const showCountBadge = hasFetcher && count !== undefined && count > 0;
		return { hasFetcher, count, isFetching, showCountBadge };
	};

	/**
	 * Aggregate badge state for the "More" overflow button: sums the counts of any badged nav
	 * items currently hidden in the overflow so the total surfaces on the More icon (otherwise the
	 * user wouldn't see a pending badge without opening More). Mirrors the per-item three-state
	 * shape — a number when > 0, otherwise a spinner while any hidden item is still fetching.
	 */
	const getOverflowBadgeState = (items: TopItem[]): OverflowBadgeState => {
		let count = 0;
		let isFetching = false;
		for (const item of items) {
			if (item.kind !== "nav") continue;
			const badge = getItemBadgeState(item.link.to);
			if (badge.showCountBadge) count += badge.count ?? 0;
			if (badge.isFetching) isFetching = true;
		}
		return { count, isFetching, showCountBadge: count > 0 };
	};

	return {
		user,
		topItems,
		handleNavClick,
		handleLogout,
		handleClearCache,
		handleOpenFeedback,
		getItemBadgeState,
		getOverflowBadgeState,
	};
}
